import type { AxiosError } from "axios"
import axios from "axios"
import { z } from "zod"

import { ISODateTimeScheme } from "@p-ban/common/src/zodCustomScheme"

import constants from "../../features/coupon/constants"

export const END_POINT = {
  COUPON_CODE: "/kiban/com/csComCouponCode.do",
  COUPON_LIST: "/kiban/com/csComCouponList.do",
  COUPON_APPLY: "/kiban/com/csComCouponApply.do",
  COUPON_ACQUIRE: "/kiban/com/csComCouponAcq.do",
} as const

type CouponCodeResponse = {
  couponCode: string
}

const couponScheme = z.object({
  id: z.number(),
  couponName: z.string(),
  discountVolume: z.number(),
  /**
   * 指定金額の値引きの場合 \
   * 指定割合の割引きの場合 %
   */
  discountType: z.literal("\\").or(z.literal("%")),
  condition: z.object({
    and: z.tuple([
      z.object({ service: z.nativeEnum(constants.service) }),
      z.object({ serviceDetail: z.nativeEnum(constants.serviceDetail).nullable() }),
      z.object({ lowerBoundPrice: z.number() }),
    ]),
  }),
  expirationDate: ISODateTimeScheme,
  applied: z.string().nullable(),
  appliedReceiptNo: z.string().nullable(),
  applicable: z.boolean(),
  appliedSession: z.boolean(),
})
export type Coupon = z.infer<typeof couponScheme>

type CouponListResponse = {
  couponList: Coupon[]
}

const couponApi = {
  async couponCode() {
    const res = await axios.get<CouponCodeResponse>(END_POINT.COUPON_CODE)

    return res.data
  },
  async couponList() {
    let { data } = await axios.get<CouponListResponse>(END_POINT.COUPON_LIST, {
      params: {
        location: (window.top as Window).location.href,
      },
    })

    data = z.object({ couponList: z.array(couponScheme) }).parse(data)

    return data
  },
  async couponApply(id: number | null) {
    return axios
      .post<"">(END_POINT.COUPON_APPLY, {
        id,
        location: (window.top as Window).location.href,
      })
      .then((res) => ({ isError: false, data: res.data } as const))
      .catch((e: AxiosError<{ errorMessage: string }>) => {
        if (e.response) {
          return { isError: true, data: e.response.data } as const
        }
        throw e
      })
  },
  async couponAcquire(couponCode: string) {
    return axios
      .post<{ couponName: string | null }>(END_POINT.COUPON_ACQUIRE, { couponCode })
      .then((res) => ({ isError: false, data: res.data } as const))
      .catch((e: AxiosError<{ errorMessage: string }>) => {
        return {
          isError: true,
          data: { errorMessage: e.response?.data.errorMessage ?? "エラーが発生しました。" },
        } as const
      })
  },
} as const

export default couponApi
